/**
 * <es-video-testimonials> - an endless, centred slider of short videos (Video testimonial slider section).
 *
 * The centre card is the active one: it is scaled up, undimmed, and plays its video muted and inline. The cards on
 * either side stay visible, dimmed; clicking one brings it to the centre. Arrows, swipe and mouse drag move one card
 * at a time, and the row never ends: the section's cards are rendered once and this element adds one copy of the set
 * before and after them, then jumps back to the real card (invisibly) whenever a move lands on a copy.
 *
 * Playback rules:
 *   - Only the centre video plays. Moving pauses it; the new centre video starts once the move has settled.
 *   - Sound is one setting for the whole slider: unmuting carries over to every card until muted again.
 *   - The play / pause button (and a click on the centre card) pauses. A visitor who paused is never auto-played or
 *     auto-advanced again until they press play (WCAG 2.2.2).
 *   - Nothing plays or moves by itself under prefers-reduced-motion, or while the slider is off screen.
 *   - "Move automatically": a playing video advances when it ends; a card without a playing video advances after the
 *     interval. Hovering or focusing the slider, or selecting a block in the Theme Editor, holds it.
 *   - Unmuting announces itself with the theme's `es-video:play` event, so other theme videos stop; another video
 *     starting with sound pauses this one.
 *
 * Copies are aria-hidden, carry no editor block attributes, and their controls are removed from the tab order.
 * Geometry is measured from layout offsets (which transforms do not affect), so the same maths serves Arabic: in
 * RTL the row is laid out from the right and "next" moves toward the left.
 *
 * UI strings come from data attributes rendered by the section; nothing user-facing is written here.
 */
class ESVideoTestimonials extends HTMLElement {
  constructor() {
    super();
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onTrackClick = this.onTrackClick.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onMediaEvent = this.onMediaEvent.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);
    this.onHoldStart = this.onHoldStart.bind(this);
    this.onHoldEnd = this.onHoldEnd.bind(this);
    this.onOtherPlay = this.onOtherPlay.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);

    this.slides = [];
    this.count = 0;
    this.index = 0;
    this.muted = true;
    this.userPaused = false;
    this.inView = false;
    this.held = false;
    this.editorHold = false;
    this.drag = null;
    this.dragged = false;
    this.settleTimer = null;
    this.advanceTimer = null;
    this.frame = null;
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.motion = null;
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    if (!this.viewport || !this.track) return;

    // A re-connect (the element moved in the DOM) must not stack a second set of copies.
    this.track.querySelectorAll(':scope > [data-clone]').forEach((clone) => clone.remove());

    const originals = Array.from(this.track.querySelectorAll(':scope > [data-slide]'));
    this.count = originals.length;
    if (this.count === 0) return;

    if (this.count > 1) this.buildLoop(originals);
    this.slides = Array.from(this.track.querySelectorAll(':scope > [data-slide]'));
    this.index = this.count > 1 ? this.count : 0;

    // The controls name each video; the element itself is decorative.
    this.track.querySelectorAll('video').forEach((video) => {
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
    });

    this.autoAdvance = this.hasAttribute('autoplay');
    this.autoVideo = this.hasAttribute('play-videos');
    this.interval = (parseFloat(this.getAttribute('interval')) || 5) * 1000;
    this.speed = parseInt(this.getAttribute('speed'), 10) || 400;
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.track.addEventListener('click', this.onTrackClick);
    this.viewport.addEventListener('click', this.onClickCapture, true);
    this.viewport.addEventListener('dragstart', this.onDragStart);
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
    // Media events do not bubble; a capturing listener on the track sees every video's.
    ['play', 'pause', 'ended'].forEach((type) => this.track.addEventListener(type, this.onMediaEvent, true));
    this.addEventListener('pointerenter', this.onHoldStart);
    this.addEventListener('pointerleave', this.onHoldEnd);
    this.addEventListener('focusin', this.onHoldStart);
    this.addEventListener('focusout', this.onHoldEnd);
    this.motion.addEventListener('change', this.onMotionChange);
    document.addEventListener('es-video:play', this.onOtherPlay);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.onIntersect, { threshold: 0.25 });
      this.intersectionObserver.observe(this);
    } else {
      this.inView = true;
    }

    this.markActive();
    this.position(false);
    this.refreshPlayback();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    if (this.track) {
      this.track.removeEventListener('click', this.onTrackClick);
      ['play', 'pause', 'ended'].forEach((type) => this.track.removeEventListener(type, this.onMediaEvent, true));
    }
    if (this.viewport) {
      this.viewport.removeEventListener('click', this.onClickCapture, true);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
    }
    this.removeEventListener('pointerenter', this.onHoldStart);
    this.removeEventListener('pointerleave', this.onHoldEnd);
    this.removeEventListener('focusin', this.onHoldStart);
    this.removeEventListener('focusout', this.onHoldEnd);
    this.motion?.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('es-video:play', this.onOtherPlay);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;

    clearTimeout(this.settleTimer);
    clearTimeout(this.advanceTimer);
    this.settleTimer = null;
    this.advanceTimer = null;
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    this.slides.forEach((slide) => slide.querySelector('video')?.pause());
    this.slides = [];
    this.drag = null;
  }

  /* One copy of the set before the real cards and one after, so a card is always visible on both sides. */
  buildLoop(originals) {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    originals.forEach((slide) => {
      before.appendChild(this.cloneSlide(slide));
      after.appendChild(this.cloneSlide(slide));
    });
    this.track.prepend(before);
    this.track.append(after);
  }

  cloneSlide(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    clone.classList.remove('is-active', 'is-playing', 'has-played');
    [clone, ...clone.querySelectorAll('[id]')].forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('button, a, video').forEach((element) => {
      element.tabIndex = -1;
    });
    return clone;
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  activeSlide() {
    return this.slides[this.index] || null;
  }

  activeVideo() {
    return this.activeSlide()?.querySelector('video') || null;
  }

  /* Horizontal offset that puts the card at `index` in the middle of the viewport. */
  offsetFor(index) {
    const slide = this.slides[index];
    if (!slide) return 0;
    const centre = this.track.offsetLeft + slide.offsetLeft + slide.offsetWidth / 2;
    return this.viewport.clientWidth / 2 - centre;
  }

  position(animate, extra = 0) {
    if (!this.track) return;
    this.track.classList.toggle('is-animating', animate && !this.motion.matches);
    this.track.style.transform = `translate3d(${this.offsetFor(this.index) + extra}px, 0, 0)`;
  }

  markActive() {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
  }

  next() {
    this.goTo(this.index + 1);
  }

  prev() {
    this.goTo(this.index - 1);
  }

  goTo(target) {
    // A move already in flight finishes first; its loop jump keeps the index inside the three sets.
    if (this.count < 2 || this.settleTimer !== null || target === this.index) return;

    clearTimeout(this.advanceTimer);
    this.advanceTimer = null;
    this.index = target;
    this.slides.forEach((slide) => slide.querySelector('video')?.pause());
    this.markActive();
    this.position(true);

    const duration = this.motion.matches ? 0 : this.speed;
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      this.settle();
    }, duration + 20);
  }

  /* Landed on a copy: swap to the real card in the same spot, with transitions off so nothing visibly moves. */
  settle() {
    let normalized = this.index;
    if (normalized >= this.count * 2) normalized -= this.count;
    else if (normalized < this.count) normalized += this.count;

    if (normalized !== this.index) {
      this.index = normalized;
      this.track.classList.add('is-jumping');
      this.markActive();
      this.position(false);
      void this.track.offsetWidth;
      this.track.classList.remove('is-jumping');
    }

    this.refreshPlayback();
  }

  canMoveByItself() {
    return (
      this.autoAdvance &&
      this.count > 1 &&
      this.inView &&
      !this.held &&
      !this.editorHold &&
      !this.userPaused &&
      !this.motion.matches
    );
  }

  refreshPlayback() {
    clearTimeout(this.advanceTimer);
    this.advanceTimer = null;

    const active = this.activeSlide();
    this.slides.forEach((slide) => {
      if (slide !== active) slide.querySelector('video')?.pause();
    });

    const video = this.activeVideo();
    if (video) {
      video.muted = this.muted;
      // Moving on when the video ends needs it not to loop.
      video.loop = !this.autoAdvance;

      if (this.inView && this.autoVideo && !this.userPaused && !this.motion.matches) {
        video.play().catch(() => this.scheduleAdvance());
        return;
      }
      video.pause();
    }

    this.scheduleAdvance();
  }

  scheduleAdvance() {
    clearTimeout(this.advanceTimer);
    this.advanceTimer = null;
    if (!this.canMoveByItself()) return;

    // A playing video moves on from its `ended` event instead.
    const video = this.activeVideo();
    if (video && !video.paused) return;

    this.advanceTimer = setTimeout(() => {
      this.advanceTimer = null;
      this.next();
    }, this.interval);
  }

  togglePlay() {
    const video = this.activeVideo();
    if (!video) return;

    if (video.paused) {
      this.userPaused = false;
      video.muted = this.muted;
      video.play().catch(() => {});
      if (!this.muted) this.announcePlay();
    } else {
      this.userPaused = true;
      video.pause();
      clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }
  }

  setMuted(muted) {
    this.muted = muted;
    this.classList.toggle('is-unmuted', !muted);
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (video) video.muted = muted;
    });
    this.querySelectorAll('[data-mute]').forEach((button) => {
      button.setAttribute('aria-label', muted ? button.dataset.labelUnmute : button.dataset.labelMute);
    });

    const video = this.activeVideo();
    if (!muted && video && !video.paused) this.announcePlay();
  }

  announcePlay() {
    document.dispatchEvent(new CustomEvent('es-video:play', { detail: { source: this } }));
  }

  syncToggle(slide) {
    const toggle = slide.querySelector('[data-toggle]');
    const video = slide.querySelector('video');
    if (!toggle || !video) return;
    const paused = video.paused;
    toggle.toggleAttribute('data-paused', paused);
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  }

  onMediaEvent(event) {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const slide = video.closest('[data-slide]');
    if (!slide) return;

    if (event.type === 'play') {
      slide.classList.add('is-playing', 'has-played');
      if (slide === this.activeSlide()) {
        clearTimeout(this.advanceTimer);
        this.advanceTimer = null;
      }
    } else {
      slide.classList.remove('is-playing');
    }
    this.syncToggle(slide);

    if (event.type === 'ended' && slide === this.activeSlide()) {
      if (this.canMoveByItself()) this.next();
      else this.scheduleAdvance();
    }
  }

  onPrevClick() {
    this.prev();
  }

  onNextClick() {
    this.next();
  }

  onTrackClick(event) {
    const slide = event.target.closest('[data-slide]');
    if (!slide || slide.parentElement !== this.track) return;

    const index = this.slides.indexOf(slide);
    if (index !== this.index) {
      event.preventDefault();
      this.goTo(index);
      return;
    }

    if (event.target.closest('[data-mute]')) {
      this.setMuted(!this.muted);
    } else if (event.target.closest('[data-toggle], [data-card]')) {
      this.togglePlay();
    }
  }

  /* The click that ends a drag must not also select a card or toggle playback. */
  onClickCapture(event) {
    if (!this.dragged) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onDragStart(event) {
    event.preventDefault();
  }

  onPointerDown(event) {
    if (this.count < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
    this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, active: false };
    this.dragged = false;
  }

  onPointerMove(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;

    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;

    if (!drag.active) {
      // Mostly vertical: the visitor is scrolling the page, not the slider.
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        this.drag = null;
        return;
      }
      if (Math.abs(dx) < 10 || this.settleTimer !== null) return;

      drag.active = true;
      this.dragged = true;
      clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
      // Captured only once it is really a drag, so ordinary clicks on buttons still reach them.
      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        // The pointer may already be gone; the drag still works without capture.
      }
    }

    drag.dx = dx;
    this.position(false, dx);
  }

  onPointerUp(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.drag = null;
    if (!drag.active) return;

    const width = this.activeSlide()?.offsetWidth || 300;
    const threshold = Math.min(60, width * 0.15);
    const towardEnd = this.isRtl ? drag.dx > threshold : drag.dx < -threshold;
    const towardStart = this.isRtl ? drag.dx < -threshold : drag.dx > threshold;

    if (event.type !== 'pointercancel' && towardEnd) {
      this.next();
    } else if (event.type !== 'pointercancel' && towardStart) {
      this.prev();
    } else {
      this.position(true);
      this.scheduleAdvance();
    }

    // Cleared after the click that follows this pointerup has been swallowed.
    setTimeout(() => {
      this.dragged = false;
    }, 0);
  }

  onResize() {
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.drag?.active) this.position(false);
    });
  }

  onIntersect(entries) {
    const entry = entries[entries.length - 1];
    this.inView = entry.isIntersecting;

    if (this.inView) {
      this.refreshPlayback();
    } else {
      this.activeVideo()?.pause();
      clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }
  }

  onMotionChange() {
    this.refreshPlayback();
  }

  onHoldStart() {
    this.held = true;
    clearTimeout(this.advanceTimer);
    this.advanceTimer = null;
  }

  onHoldEnd(event) {
    if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
    this.held = false;
    this.scheduleAdvance();
  }

  onOtherPlay(event) {
    if (event.detail?.source === this) return;
    const video = this.activeVideo();
    if (video && !video.paused && !video.muted) {
      this.userPaused = true;
      video.pause();
    }
  }

  onBlockSelect(event) {
    const index = this.slides.indexOf(event.target);
    if (index < 0) return;

    this.editorHold = true;
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
      this.settle();
    }
    if (index !== this.index) this.goTo(index);
  }

  onBlockDeselect(event) {
    if (this.slides.indexOf(event.target) < 0) return;
    this.editorHold = false;
    this.scheduleAdvance();
  }
}

if (!customElements.get('es-video-testimonials')) {
  customElements.define('es-video-testimonials', ESVideoTestimonials);
}
